/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDeviceSipProfileList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing Device SIP Profile list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchDeviceSipProfiles } from '../api';
import { DeviceSipProfile } from '../types';

interface UseDeviceSipProfileListProps {
  initialColumns?: DataGridColumn<DeviceSipProfile>[];
  domainId?: number | null;
  onRefreshNeeded?: () => void;
}

/**
 * Custom React hook for managing the Device SIP Profiles DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * Supports Domain Contextual Filtering to only show profiles belonging to the current tenant.
 * 
 * @param props - Configuration properties for the list hook.
 * @returns Combined DataGrid state and the effective Domain ID applied to the filter.
 */
export function useDeviceSipProfileList(props: UseDeviceSipProfileListProps = {}) {
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchProfiles = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const result = await fetchDeviceSipProfiles({
        ...params,
        domainId: effectiveDomainId,
      });
      return {
        data: result.data || [],
        pagination: result.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      };
    } catch (error) {
      return {
        data: [] as DeviceSipProfile[],
        pagination: {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }, [effectiveDomainId]);

  const dataGridResult = useDataGrid({
    fetchData: fetchProfiles,
    initialColumns: props.initialColumns || [],
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'deviceSipProfiles',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...dataGridResult,
    effectiveDomainId,
  };
}

